import React, { useEffect, useState } from "react";
import { Container, Modal, Form, Button, Row, Col, Table, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { message, Modal as AntModal } from "antd";
import moment from "moment";
import Footer from "../../../components/superAdmin/footer/Footer";
import SubAdminTable from "../../../components/subadmin/SubAdminTable";
import ShowDetailsModal from "../../../common/ShowDetailsModal";
import SubAdminHeader from "../components/SubAdminHeader";

const SubAdminExhibitors = () => {
    const navigate = useNavigate();
    const [exhibitors, setExhibitors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [permissions, setPermissions] = useState(null);
    
    // Modals state
    const [showDetails, setShowDetails] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [showEdit, setShowEdit] = useState(false);
    const [editForm, setEditForm] = useState({});
    const [saving, setSaving] = useState(false);
    
    // Members List State
    const [showMembers, setShowMembers] = useState(false);
    const [membersData, setMembersData] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [memberType, setMemberType] = useState(''); // 'owner', 'staff', 'vip'
    const [currentExhibitor, setCurrentExhibitor] = useState(null);

    const fetchData = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('jff_token');
            const dashResponse = await axios.get(`${process.env.REACT_APP_API_END_POINT}/subadmin/dashboard`, {
                headers: { token }
            });
            const perms = dashResponse.data.permissions.exhibitorList;
            setPermissions(perms);

            if (!perms.enabled || !perms.read) {
                navigate("/subadmin/dashboard");
                return;
            }

            const response = await axios.get(`${process.env.REACT_APP_API_END_POINT}/subadmin/exhibitors`, {
                headers: { token }
            });
            setExhibitors(response.data.data);
        } catch (error) {
            console.error("Error fetching exhibitors:", error);
            if (error.response && error.response.status === 403) {
                navigate("/subadmin/dashboard");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [navigate]);

    const handleView = (item) => {
        setSelectedItem(item);
        setShowDetails(true);
    };

    const handleEdit = (item) => {
        setEditForm({ ...item });
        setShowEdit(true);
    };

    const handleUpdate = async () => {
        setSaving(true);
        try {
            const token = localStorage.getItem('jff_token');
            await axios.post(`${process.env.REACT_APP_API_END_POINT}/update-exhibitor`, {
                id: editForm._id,
                company_name: editForm.company_name,
                contact_person: editForm.contact_person,
                mobile_no: editForm.mobile_no,
                email: editForm.email,
                coordinator_name: editForm.coordinator_name,
                coordinator_mobile_no: editForm.coordinator_mobile_no
            }, {
                headers: { token }
            });
            message.success("Updated successfully");
            setShowEdit(false);
            fetchData();
        } catch (error) {
            message.error("Failed to update");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (id) => {
        AntModal.confirm({
            title: 'Are you sure you want to delete this exhibitor?',
            content: 'This action cannot be undone.',
            okText: 'Yes, Delete',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const token = localStorage.getItem('jff_token');
                    await axios.post(`${process.env.REACT_APP_API_END_POINT}/delete-exhibitor`, { id, delete_status: "0" }, {
                        headers: { token }
                    });
                    message.success("Deleted successfully");
                    fetchData();
                } catch (error) {
                    message.error("Failed to delete");
                }
            }
        });
    };
    
    const handleMembersClick = async (item, type) => {
        setCurrentExhibitor(item);
        setMemberType(type);
        setShowMembers(true);
        setMembersLoading(true);
        
        try {
            const token = localStorage.getItem('jff_token');
            // Determine category based on type
            let category = '';
            if (type === 'owner') category = process.env.REACT_APP_EXHIBITOR_OWNER;
            else if (type === 'staff') category = process.env.REACT_APP_EXHIBITOR_STAFF;
            else if (type === 'vip') category = process.env.REACT_APP_EXHIBITOR_VIP;
            
            const response = await axios.post(`${process.env.REACT_APP_API_END_POINT}/exhibitor-members-list`, 
                { firm_id: item._id, category },
                { headers: { token } }
            );
            setMembersData(response.data.data);
        } catch (error) {
            console.error("Error fetching members:", error);
            message.error("Failed to fetch members list");
        } finally {
            setMembersLoading(false);
        }
    };

    const handleStatusChange = async (item) => {
        try {
            const token = localStorage.getItem('jff_token');
            let newStatus = item.status === "active" ? "inactive" : "active";
            
            await axios.post(`${process.env.REACT_APP_API_END_POINT}/update-exhibitor-status`, 
                { id: item._id, status: newStatus },
                { headers: { token } }
            );
            
            message.success("Status updated successfully");
            fetchData();
        } catch (error) {
            console.error("Error updating status:", error);
            message.error("Failed to update status");
        }
    };

    return (
        <div style={{ backgroundColor: "#fff", minHeight: "100vh" }}>
            <SubAdminHeader />

            <Container fluid className="px-0 py-5">
                <div className="text-center mb-4">
                    <h2 className="form-heading">Exhibitor List - {exhibitors.length}</h2>
                </div>
                <SubAdminTable 
                    data={exhibitors}
                    columns={permissions?.columns || []}
                    loading={loading}
                    writeAccess={permissions?.write}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onMembersClick={
                        (permissions?.columns?.includes('exhibitor_owner') || 
                         permissions?.columns?.includes('exhibitor_staff') || 
                         permissions?.columns?.includes('exhibitor_vip')) 
                        ? handleMembersClick : null
                    }
                    onStatusChange={handleStatusChange}
                    tableName="Exhibitor List"
                />
            </Container>

            <ShowDetailsModal 
                showAllDetails={showDetails} 
                setShowAllDetails={setShowDetails} 
                data={selectedItem} 
                allowedColumns={permissions?.columns}
            />

            <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Exhibitor</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Company Name</Form.Label>
                                    <Form.Control 
                                        value={editForm.company_name || ''} 
                                        onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Contact Person</Form.Label>
                                    <Form.Control 
                                        value={editForm.contact_person || ''} 
                                        onChange={(e) => setEditForm({ ...editForm, contact_person: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Mobile No.</Form.Label>
                                    <Form.Control 
                                        value={editForm.mobile_no || ''} 
                                        onChange={(e) => setEditForm({ ...editForm, mobile_no: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Email</Form.Label>
                                    <Form.Control 
                                        value={editForm.email || ''} 
                                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Coordinator Name</Form.Label>
                                    <Form.Control 
                                        value={editForm.coordinator_name || ''} 
                                        onChange={(e) => setEditForm({ ...editForm, coordinator_name: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>Coordinator Mobile No.</Form.Label>
                                    <Form.Control 
                                        value={editForm.coordinator_mobile_no || ''} 
                                        maxLength={10}
                                        onChange={(e) => setEditForm({ ...editForm, coordinator_mobile_no: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowEdit(false)}>Cancel</Button>
                    <Button variant="primary" onClick={handleUpdate} disabled={saving}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Modal>
            
            {/* Members List Modal */}
            <Modal show={showMembers} onHide={() => setShowMembers(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {memberType.charAt(0).toUpperCase() + memberType.slice(1)} List - {currentExhibitor?.company_name}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {membersLoading ? (
                        <div className="text-center py-5"><Spinner animation="border" variant="warning" /></div>
                    ) : (
                        <div className="table-responsive">
                            <Table striped bordered hover>
                                <thead style={{ backgroundColor: '#b98b51', color: '#fff' }}>
                                    <tr>
                                        <th>#</th>
                                        <th>Reg. No.</th>
                                        <th>Name</th>
                                        <th>Mobile</th>
                                        <th>Reg. Date</th>
                                        <th>Photo</th>
                                        <th>ID Proof</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {membersData.length > 0 ? (
                                        membersData.map((member, idx) => (
                                            <tr key={member._id || idx}>
                                                <td>{idx + 1}</td>
                                                <td>{member.barcode}</td>
                                                <td>{member.visitor_name}</td>
                                                <td>{member.mobile_number}</td>
                                                <td>{moment(member.createdAt).format("MMM Do YYYY")}</td>
                                                <td>
                                                    {member.photo ? (
                                                        <img 
                                                            src={`${process.env.REACT_APP_IMAGE_URL}/photo/${member.photo}?v=${moment().unix()}`}
                                                            alt="Photo"
                                                            style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                                        />
                                                    ) : '-'}
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-1 justify-content-center">
                                                        {member.id_proof_front && (
                                                            <img 
                                                                src={`${process.env.REACT_APP_IMAGE_URL}/id_proof/${member.id_proof_front}?v=${moment().unix()}`}
                                                                alt="ID Front"
                                                                style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                                            />
                                                        )}
                                                        {member.id_proof_back && (
                                                            <img 
                                                                src={`${process.env.REACT_APP_IMAGE_URL}/id_proof/${member.id_proof_back}?v=${moment().unix()}`}
                                                                alt="ID Back"
                                                                style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                                            />
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-4">No members found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowMembers(false)}>Close</Button>
                </Modal.Footer>
            </Modal>
            
            <Footer />
        </div>
    );
};

export default SubAdminExhibitors;
